import { joiResolver } from '@hookform/resolvers/joi';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import LinkIcon from '@mui/icons-material/Link';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
  Autocomplete,
  Box,
  Button,
  Fab,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import jsQR from 'jsqr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Footer from '@/components/atoms/Footer';
import Logo from '@/components/atoms/Logo';
import { PasswordField } from '@/components/atoms/PasswordField';
import useLanguage, { useSetLanguage } from '@/components/hooks/useLanguage';
import useLoadingBackdrop from '@/components/hooks/useLoadingBackdrop';
import useLocalizedJoi from '@/components/hooks/useLocalizedJoi';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import LanguageSwitcher from '@/components/molecules/LanguageSwitcher';
import { useResolveGuideRoomLoginLazyQuery } from '@/types/graphql';
import {
  GuideLoginCandidate,
  listGuideLoginCandidates,
  removeGuideLoginCandidate,
  saveGuideLoginCandidate,
} from '@/utils/guideLoginCandidates';

type GuideLoginInput = {
  guideLoginId: string;
  guideLoginPassword: string;
};

const Home = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { t } = useTranslation('common');

  const language = useLanguage();
  const setLanguage = useSetLanguage();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const intervalRef = useRef<number>();
  const [cameraState, setCameraState] = useState<'off' | 'on'>('off');
  const [url, setUrl] = useState<string>('');
  const [urlError, setUrlError] = useState(false);
  const loadingBackdrop = useLoadingBackdrop();

  const stopCameraStream = useCallback(() => {
    // コンポーネントがアンマウントされたらカメラストリームを停止
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.style.display = 'none';
      // eslint-disable-next-line react-hooks/exhaustive-deps
      (videoRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      setCameraState('off');
    }
  }, []);

  const scanQRCode = useCallback(() => {
    if (canvasRef.current && videoRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (
        context &&
        videoRef.current?.videoWidth > 0 &&
        videoRef.current?.videoHeight > 0
      ) {
        const videoWidth = videoRef.current.videoWidth;
        const videoHeight = videoRef.current.videoHeight;

        canvas.width = videoWidth;
        canvas.height = videoHeight;
        context.drawImage(videoRef.current, 0, 0);

        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code?.data != null) {
          try {
            const url = new URL(code.data);
            const pathOnly = `${url.pathname}${url.search}${url.hash}`;
            stopCameraStream();
            navigate(pathOnly);
          } catch (error) {
            console.error('Invalid URL:', error);
          }
        }
      }
    }
  }, [navigate, stopCameraStream]);

  const startCamera = useCallback(async () => {
    try {
      await loadingBackdrop.open(async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        await new Promise<void>((resolve, reject) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;

            // 動画がロードされたタイミングで再生を開始
            videoRef.current.onloadedmetadata = () => {
              videoRef.current
                ?.play()
                .then(() => {
                  intervalRef.current = window.setInterval(scanQRCode, 500);
                  resolve();
                })
                .catch((error) => {
                  reject(error);
                });
            };
          }
        });
      });
      setCameraState('on');
    } catch (e) {
      setCameraState('off');
      showSnackbar('error', t('home.cameraStartFailed'));
      console.error(e);
    }
  }, [loadingBackdrop, scanQRCode, showSnackbar, t]);

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, [stopCameraStream]);

  // ガイド・管理者ログイン
  const [candidates, setCandidates] = useState<GuideLoginCandidate[]>([]);
  useEffect(() => {
    setCandidates(listGuideLoginCandidates());
  }, []);

  const joi = useLocalizedJoi();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<GuideLoginInput>({
    defaultValues: { guideLoginId: '', guideLoginPassword: '' },
    resolver: joiResolver(
      joi.object<GuideLoginInput>({
        guideLoginId: joi
          .string()
          .trim()
          .required()
          .length(10)
          .regex(/^\d+$/),
        guideLoginPassword: joi
          .string()
          .trim()
          .required()
          .length(6)
          .regex(/^\d+$/),
      })
    ),
  });

  const [resolveGuideRoomLogin, { loading: isGuideLoginSubmitting }] =
    useResolveGuideRoomLoginLazyQuery();

  const guideLogin = async (input: GuideLoginInput) => {
    const result = await resolveGuideRoomLogin({
      variables: {
        input: {
          guideLoginId: input.guideLoginId,
          guideLoginPassword: input.guideLoginPassword,
        },
      },
    });
    const data = result.data?.resolveGuideRoomLogin;
    if (
      data?.isValid !== true ||
      data.chatRoomId == null ||
      data.chatUserId == null ||
      data.chatUserPassword == null
    ) {
      showSnackbar('error', t('home.guideLoginInvalid'));
      return;
    }
    // localStorageの候補にはユーザーが入力した「ルームID/パスワード」（数字10桁/6桁）を保存する。
    // 実際のURL遷移には、この部屋専属で発行された本物のchatUserId/chatUserPassword（UUID）を使い、
    // 既存のChatLogin.tsx以降の入室フロー（notifyJoin等、UUID前提の処理）をそのまま流用する。
    saveGuideLoginCandidate(input.guideLoginId, input.guideLoginPassword);
    navigate(
      `/chat/${data.chatRoomId}?chatUserId=${encodeURIComponent(
        data.chatUserId
      )}&chatUserPassword=${encodeURIComponent(data.chatUserPassword)}`
    );
  };

  const selectCandidate = (candidate: GuideLoginCandidate | null) => {
    if (candidate == null) return;
    setValue('guideLoginId', candidate.guideLoginId);
    setValue('guideLoginPassword', candidate.guideLoginPassword);
  };

  const deleteCandidate = (guideLoginId: string) => {
    removeGuideLoginCandidate(guideLoginId);
    setCandidates(listGuideLoginCandidates());
  };

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          backgroundColor: 'black',
          display: cameraState === 'on' ? 'block' : 'none',
          width: '100vw',
          height: '100dvh',
          objectFit: 'contain',
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'scaleX(1)', // 反転を解除
          zIndex: 100,
        }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
      {cameraState === 'on' && (
        <Fab
          color="primary"
          aria-label="add"
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 101 }}
          onClick={() => {
            stopCameraStream();
          }}
        >
          <CloseIcon />
        </Fab>
      )}
      <Stack
        justifyContent="center"
        alignItems="center"
        width="100vw"
        minHeight="100dvh"
        sx={{ bgcolor: (theme) => theme.palette.background.default }}
      >
        <Stack
          flex={1}
          justifyContent="center"
          alignItems="center"
          spacing={3}
          width={1}
          p={2}
          maxWidth={480}
        >
          <Logo />
          <Box width={1}>
            <LanguageSwitcher language={language} setLanguage={setLanguage} />
          </Box>

          {/* 一般参加者 */}
          <Stack
            width={1}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: (theme) => `1px solid ${theme.palette.orange.main}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              p={1.5}
              sx={{ bgcolor: (theme) => theme.palette.orange.main }}
            >
              <PeopleAltIcon sx={{ color: '#fff' }} />
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t('home.generalParticipant')}
              </Typography>
            </Stack>
            <Stack
              spacing={2}
              p={2}
              sx={{ bgcolor: (theme) => theme.palette.orange.light }}
            >
              <Button
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => startCamera()}
                sx={{
                  color: (theme) => theme.palette.orange.dark,
                  borderColor: (theme) => theme.palette.orange.main,
                }}
              >
                {t('home.scanQrCode')}
              </Button>
              <Typography
                variant="caption"
                textAlign="center"
                color="text.secondary"
              >
                {t('home.or')}
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                label={t('home.enterUrl')}
                placeholder={t('home.urlPlaceholder')}
                onChange={(ev) => {
                  setUrlError(false);
                  setUrl(ev.target.value);
                }}
                error={urlError}
                helperText={urlError ? t('home.invalidUrl') : undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'background.paper' }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: (theme) => theme.palette.orange.main,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.orange.dark,
                  },
                }}
                onClick={() => {
                  let urloptions: URL | undefined;
                  try {
                    urloptions = new URL(url);
                  } catch (e) {
                    setUrlError(true);
                  }

                  if (urloptions != null) {
                    // オリジンが一致しないリクエストははじく
                    if (window.location.origin !== urloptions.origin) {
                      setUrlError(true);
                      return;
                    } else {
                      const pathOnly = `${urloptions.pathname}${urloptions.search}${urloptions.hash}`;
                      navigate(pathOnly);
                    }
                  }
                }}
              >
                {t('home.join')}
              </Button>
            </Stack>
          </Stack>

          {/* ガイド・管理者 */}
          <Stack
            width={1}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
            }}
            component="form"
            onSubmit={(ev) => {
              ev.preventDefault();
              handleSubmit(guideLogin)();
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              p={1.5}
              sx={{ bgcolor: (theme) => theme.palette.primary.main }}
            >
              <FlagIcon sx={{ color: '#fff' }} />
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t('home.guideAdmin')}
              </Typography>
            </Stack>
            <Stack spacing={2} p={2}>
              <Autocomplete
                freeSolo
                size="small"
                options={candidates}
                inputValue={watch('guideLoginId')}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.guideLoginId
                }
                isOptionEqualToValue={(option, value) =>
                  option.guideLoginId ===
                  (typeof value === 'string' ? value : value.guideLoginId)
                }
                onInputChange={(_, newInputValue) => {
                  setValue(
                    'guideLoginId',
                    newInputValue.replace(/\D/g, '').slice(0, 10),
                    { shouldValidate: true }
                  );
                }}
                onChange={(_, newValue) => {
                  if (newValue != null && typeof newValue !== 'string') {
                    selectCandidate(newValue);
                  }
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.guideLoginId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {option.guideLoginId}
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={t('home.deleteCandidate')}
                      sx={{ ml: 'auto' }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        deleteCandidate(option.guideLoginId);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('home.roomId')}
                    error={'guideLoginId' in errors}
                    helperText={errors.guideLoginId?.message}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlagIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 10,
                    }}
                  />
                )}
              />
              <PasswordField
                variant="outlined"
                size="small"
                label={t('home.password')}
                {...register('guideLoginPassword')}
                value={watch('guideLoginPassword')}
                error={'guideLoginPassword' in errors}
                helperText={errors.guideLoginPassword?.message}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isGuideLoginSubmitting}
              >
                {t('home.join')}
              </Button>
            </Stack>
          </Stack>

          <Button
            variant="text"
            onClick={() => {
              navigate('/admin');
            }}
          >
            {t('home.hasAccount')}
          </Button>
        </Stack>
        <Footer />
      </Stack>
    </>
  );
};
export default Home;
